import React from "react";
import type { BoomIconName } from "./BoomIconNames";

type SvgIcon = React.ComponentType<React.SVGAttributes<SVGElement>>;

type IconModule =
  | {
      default?: SvgIcon;
    }
  | SvgIcon;

type WebpackRequireContext = {
  keys: () => string[];
  (id: string): IconModule;
};

declare const require: NodeRequire & {
  context: (
    directory: string,
    useSubdirectories: boolean,
    regExp: RegExp
  ) => WebpackRequireContext;
};

type BoomIconProps = {
  icon: BoomIconName | (string & {});
  width?: number;
  height?: number;
  className?: string;
  iconClassName?: string;
} & Omit<React.HTMLAttributes<HTMLSpanElement>, "children">;

const iconContext = require.context("../../assets/icons", true, /\.svg2$/);

const normalizeIconPath = (icon: string) => {
  const trimmedIcon = icon.trim().replace(/^\.\//, "");
  return `./${
    trimmedIcon.endsWith(".svg2") ? trimmedIcon : `${trimmedIcon}.svg2`
  }`;
};

const getIconComponent = (icon: string): SvgIcon | undefined => {
  const iconPath = normalizeIconPath(icon);

  if (!iconContext.keys().includes(iconPath)) {
    return undefined;
  }

  const iconModule = iconContext(iconPath) as IconModule;
  return typeof iconModule === "function" ? iconModule : iconModule.default;
};

export default function BoomIcon({
  icon,
  width = 30,
  height = width,
  className = "",
  iconClassName = "",
  ...props
}: BoomIconProps) {
  const Icon = getIconComponent(icon);

  if (!Icon) {
    return null;
  }

  return (
    <span
      {...props}
      className={`bg-icon rounded-circle d-inline-block position-relative ${className}`}
      style={{
        width,
        height,
        ...props.style,
      }}
    >
      <Icon
        width={width}
        height={height}
        className={`position-absolute top-50 start-50 translate-middle ${iconClassName}`}
      />
    </span>
  );
}
